import type { Toast } from "./toast.interface"
import { readonly, ref } from "vue"
import { i18n } from "@/i18n"

/**
 * Global reactive array to store all active toast notifications
 */
const toasts = ref<Toast[]>([])

/**
 * Composable for managing toast notifications throughout the application
 *
 * This composable provides a centralized way to create, manage, and remove toast notifications.
 * It maintains a global state of all active toasts and provides convenient methods for different types of notifications (success, error, info).
 */
export function useToast() {
  const { t } = i18n.global

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAllToasts = () => {
    toasts.value = []
  }

  /**
   * Adds a new toast notification to the list
   * Automatically generates a unique ID and sets up auto-removal timer if duration is specified.
   */
  const addToast = (toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast: Toast = {
      id,
      duration: 10000,
      variant: "default",
      ...toast,
    }

    toasts.value.push(newToast)

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }

    return id
  }

  /**
   * Creates and displays a success toast notification
   * Uses default success styling and icon. Override any properties by passing them in partialToast.
   */
  const success = (partialToast: Partial<Toast> = {}) => {
    const defaults = { title: t("toast_defaults.success.title"), description: t("toast_defaults.success.description"), icon: "CircleCheck" as const }
    return addToast({ ...defaults, ...partialToast, variant: "success" })
  }

  /**
   * Creates and displays an error toast notification
   * Uses default error styling and icon. Override any properties by passing them in partialToast.
   */
  const error = (partialToast: Partial<Toast> = {}) => {
    const defaults = {
      title: t("toast_defaults.error.title"),
      description: t("toast_defaults.error.description"),
      icon: "Caution" as const,
    }
    return addToast({ ...defaults, ...partialToast, variant: "danger" })
  }

  /**
   * Creates and displays an info toast notification
   * Uses default info styling and icon. Override any properties by passing them in partialToast.
   */
  const info = (partialToast: Partial<Toast> = {}) => {
    const defaults = { title: t("toast_defaults.info.title"), icon: "CircleInfo" as const }
    return addToast({ ...defaults, ...partialToast, variant: "default" })
  }

  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    info,
  }
}
